import styled from "@emotion/styled";
import { filter, map, sortBy, uniq } from "lodash-es";
import { observer } from "mobx-react-lite";
import { useMemo } from "react";

import {
  MDSCard,
  MDSCardVariant,
  MDSSkeleton,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
  MDSTextWeight,
} from "@/mds";
import { useAgentOverlayAnalytics } from "@/pages/agent/analytics/use-agent-overlay-analytics";
import { AgentFilterSearchField } from "@/pages/agent/components/agent-filter-search-field";
import { AgentFilterSelect } from "@/pages/agent/components/agent-filter-select";
import { AgentPageFilterBar, AgentPageLayout } from "@/pages/agent/components/agent-page-layout";
import { AgentSurfaceNavigationActiveTab } from "@/pages/agent/components/agent-surface-navigation";
import { useAgentIntentGraph } from "@/pages/agent/intent-graph/use-agent-intent-graph";
import { AgentProjectBreadcrumbs } from "@/pages/agent/project/agent-project-breadcrumbs";
import { AgentTaskViewBar } from "@/pages/agent/tasks/agent-task-view-bar";
import {
  DEFAULT_TASK_STATUS_FILTER_VALUES,
  getTaskRowsForView,
} from "@/pages/agent/tasks/agent-tasks-page.utils";
import { AgentTasksList } from "@/pages/agent/tasks/agent-tasks-list";
import { useAgentTaskRows } from "@/pages/agent/tasks/use-agent-task-rows";
import {
  ALL_STATUSES_FILTER,
  TaskView,
  useAgentTasksSearchParams,
} from "@/pages/agent/tasks/use-agent-tasks-search-params";
import {
  buildAgentStatusFilterItems,
  formatStatusLabel,
  normalizeStatusValue,
} from "@/pages/agent/utils/agent-status.utils";
import { useAppStore } from "@/store";
import type { AssistantProjectObservable } from "@/store/assistant";
import {
  isClosedAssistantTaskStatus,
  normalizeAssistantTaskStatus,
} from "@/store/assistant/assistant-task-status";
import { AssistantTaskStatus } from "@/store/assistant/types";

export const AgentProjectTasksPage = observer(({ projectId }: { projectId: string }) => {
  const { store } = useAppStore();
  const project = store.assistantProjects.getCurrentAssistantModelById(projectId);
  const isMissing = store.assistantProjects.currentAssistantModelDoesNotExist(projectId);

  return (
    <AgentPageLayout activeTab={AgentSurfaceNavigationActiveTab.Projects} fillsViewport>
      {project ? (
        <ProjectTasks project={project} />
      ) : isMissing ? (
        <StyledStateCard variant={MDSCardVariant.Chrome}>
          <MDSText
            color={MDSTextColor.Primary}
            variant={MDSTextVariant.HeaderS}
            weight={MDSTextWeight.SemiBold}
          >
            Project not found
          </MDSText>
          <MDSText color={MDSTextColor.Secondary} variant={MDSTextVariant.ParagraphS}>
            These tasks may belong to a project that was removed.
          </MDSText>
        </StyledStateCard>
      ) : (
        <StyledStateCard aria-label="Loading project tasks" variant={MDSCardVariant.Chrome}>
          <MDSSkeleton height={24} width="45%" />
          <MDSSkeleton height={16} width="70%" />
        </StyledStateCard>
      )}
    </AgentPageLayout>
  );
});

const ProjectTasks = observer(({ project }: { project: AssistantProjectObservable }) => {
  const { store } = useAppStore();
  const analytics = useAgentOverlayAnalytics();
  const graph = useAgentIntentGraph();
  const { statusFilter, searchQuery, viewFilter, updateTaskFilters } = useAgentTasksSearchParams({
    defaultView: TaskView.AllTasks,
  });
  const parentProjectId = graph.data?.project_hierarchy_links?.find(
    link => link.project_id === project.id
  )?.parent_project_id;
  const parentProject = parentProjectId
    ? store.assistantProjects.getCurrentAssistantModelById(parentProjectId)
    : undefined;
  const allTaskRows = useAgentTaskRows({ store });
  const taskRows = useMemo(
    () => allTaskRows.filter(taskRow => taskRow.task.projectId === project.id),
    [allTaskRows, project.id]
  );
  const dataLoaded =
    store.assistantProjects.currentAssistantModelsLoaded &&
    store.assistantTasks.currentAssistantModelsLoaded &&
    store.assistantFollowUps.currentAssistantModelsLoaded &&
    store.assistantTaskNoteReferences.currentAssistantModelsLoaded;
  const normalizedSearchQuery = searchQuery.trim().toLowerCase();
  const visibleTaskRows = useMemo(() => {
    const filteredTaskRows = filter(taskRows, taskRow => {
      const matchesStatus =
        statusFilter === ALL_STATUSES_FILTER ||
        normalizeStatusValue({ status: taskRow.task.status }) === statusFilter;
      const matchesSearch =
        !normalizedSearchQuery || taskRow.searchableText.includes(normalizedSearchQuery);

      return matchesStatus && matchesSearch;
    });

    return getTaskRowsForView({
      taskRows: filteredTaskRows,
      view: viewFilter,
    });
  }, [normalizedSearchQuery, statusFilter, taskRows, viewFilter]);
  const statusFilterValues = sortBy(
    uniq([
      ...DEFAULT_TASK_STATUS_FILTER_VALUES,
      ...map(taskRows, taskRow => normalizeStatusValue({ status: taskRow.task.status })),
    ]),
    status => formatStatusLabel({ status })
  );
  const statusFilterItems = buildAgentStatusFilterItems({
    allStatusesFilter: ALL_STATUSES_FILTER,
    statusFilter,
    statusFilterValues,
  });
  const queryKey = JSON.stringify({
    projectId: project.id,
    searchQuery: normalizedSearchQuery,
    statusFilter,
    viewFilter,
  });
  const openTask = ({ taskId }: { taskId: string }) => {
    analytics.trackItemClick({ itemId: taskId, itemKind: "task", source: "task_row" });
    store.agentOverlay.openTask({ taskId });
  };
  const openSourceNote = ({ noteId }: { noteId: string }) => {
    analytics.trackItemClick({ itemId: noteId, itemKind: "note", source: "task_source" });
    store.agentOverlay.openSourceNote({ noteId });
  };
  const toggleTaskComplete = async ({ taskId }: { taskId: string }) => {
    const task = store.assistantTasks.getCurrentAssistantModelById(taskId);
    if (!task) return;

    const currentStatus = normalizeAssistantTaskStatus({ status: task.status });
    const nextStatus = isClosedAssistantTaskStatus({ status: currentStatus })
      ? AssistantTaskStatus.Pending
      : AssistantTaskStatus.Completed;
    let action: "completed" | "marked_incomplete" | "reopened" = "completed";

    if (nextStatus !== AssistantTaskStatus.Completed) {
      action = currentStatus === AssistantTaskStatus.Completed ? "marked_incomplete" : "reopened";
    }

    analytics.trackTaskStatusToggle({ action, taskId });
    await task.updateStatus({ status: nextStatus });
  };

  return (
    <StyledPageStack>
      <AgentProjectBreadcrumbs
        currentPage="Tasks"
        parentProject={parentProject}
        project={project}
        onOpenProject={({ projectId }) => store.agentOverlay.openProject({ projectId })}
        onOpenProjects={() => store.agentOverlay.openProjects()}
      />

      <StyledHeader>
        <MDSText
          color={MDSTextColor.Primary}
          variant={MDSTextVariant.HeaderM}
          weight={MDSTextWeight.SemiBold}
        >
          Tasks
        </MDSText>
        <MDSText color={MDSTextColor.Secondary} variant={MDSTextVariant.ParagraphS}>
          All work connected to {project.title || "this project"}.
        </MDSText>
      </StyledHeader>

      <AgentTaskViewBar
        selectedView={viewFilter}
        onChange={nextView => {
          analytics.trackFilterChange({ filterKind: "view", filterValue: nextView });
          updateTaskFilters({ nextViewFilter: nextView });
        }}
      />

      <AgentPageFilterBar>
        <AgentFilterSelect
          label="Status"
          selectedKey={statusFilter}
          defaultKey={ALL_STATUSES_FILTER}
          items={statusFilterItems}
          onSelectionChange={({ key }) => {
            analytics.trackFilterChange({ filterKind: "status", filterValue: key });
            updateTaskFilters({ nextStatusFilter: key });
          }}
          onClear={() => {
            analytics.trackFilterChange({
              filterKind: "status",
              filterValue: ALL_STATUSES_FILTER,
            });
            updateTaskFilters({ nextStatusFilter: ALL_STATUSES_FILTER });
          }}
        />

        <AgentFilterSearchField
          ariaLabel="Filter project tasks"
          placeholder="Filter project tasks"
          value={searchQuery}
          onChange={value => {
            analytics.trackFilterChange({
              filterKind: "search",
              queryLength: value.trim().length,
            });
            updateTaskFilters({ nextSearchQuery: value });
          }}
        />
      </AgentPageFilterBar>

      <AgentTasksList
        dataLoaded={dataLoaded}
        queryKey={queryKey}
        showProject={false}
        taskRows={visibleTaskRows}
        onOpenActivities={({ taskId }) =>
          store.agentOverlay.openActivities({ kind: "task", taskId })
        }
        onOpenNote={openSourceNote}
        onOpenProject={({ projectId }) => store.agentOverlay.openProject({ projectId })}
        onOpenTask={openTask}
        onToggleTaskComplete={toggleTaskComplete}
      />
    </StyledPageStack>
  );
});

const StyledPageStack = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
  min-height: 0;
`;

const StyledHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const StyledStateCard = styled(MDSCard)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
`;
